import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React, { useMemo } from "react";
import { renderMarkdownToNode } from "~/hooks/useMarkdownRenderer";
import type { Document as AppDocument } from "~/interface/document";
import styles from "./index.module.scss";

interface ContentData {
  PluginProposalHeaders?: string[];
  headers?: string[];
  rows: string[][];
}

interface HistoryPluginProposalProps {
  formData: AppDocument[];
}

const decodeString = (str: string): string => {
  try {
    return decodeURIComponent(JSON.parse(`"${str}"`));
  } catch {
    return str;
  }
};

const renderMarkdown = renderMarkdownToNode;

const HistoryPluginProposal = ({ formData }: HistoryPluginProposalProps) => {
  const rawContent = formData[0]?.content;

  const FIXED_HEADERS = React.useMemo(
    () => ["fid", "必要機能", "プラグイン名", "機能一覧", "価格情報", "会社名", "サマリー"],
    [],
  );

  const { content, isMarkdown, markdownContent } = useMemo(() => {
    if (!rawContent) return { content: null, isMarkdown: false, markdownContent: "" };
    try {
      const parsed = JSON.parse(rawContent as unknown as string);
      if (Array.isArray(parsed?.rows)) {
        return { content: parsed as ContentData, isMarkdown: false, markdownContent: "" };
      }
      // Markdown形式の場合
      return {
        content: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    } catch {
      // JSONパースに失敗した場合もMarkdown扱い
      return {
        content: null,
        isMarkdown: true,
        markdownContent: rawContent as string,
      };
    }
  }, [rawContent]);

  const headers: string[] | undefined = useMemo(() => {
    if (!content) return undefined;
    return content.PluginProposalHeaders ?? content.headers;
  }, [content]);

  const columnHelper = createColumnHelper<Record<string, string>>();

  const columns = useMemo(() => {
    if (!headers || !Array.isArray(headers)) return [];

    return FIXED_HEADERS.map((header) =>
      columnHelper.accessor(header, {
        id: header,
        header: header === "サマリー" ? "提案理由" : header,
        cell: (info) => decodeString(info.getValue()),
      }),
    );
  }, [headers, columnHelper, FIXED_HEADERS]);

  const data = useMemo(() => {
    if (!content || !headers || !Array.isArray(headers) || !Array.isArray(content.rows)) return [];

    return content.rows.map((row: string[]) => {
      const obj: Record<string, string> = {};
      headers.forEach((header: string, index: number) => {
        obj[header] = row[index] || "";
      });
      return obj;
    });
  }, [headers, content]);

  const table = useReactTable<Record<string, string>>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isMarkdown) {
    return (
      <div className={`${styles["table-wrapper"]} ${styles["document-content"]}`}>
        <div className={styles["markdown-content"]}>{renderMarkdown(markdownContent || "")}</div>
      </div>
    );
  }

  return (
    <div className={styles["table-wrapper"]}>
      <table className={styles["table"]}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default HistoryPluginProposal;
